/**
 * Table — the app's data table: columns, a shimmer skeleton while loading, an
 * empty message, optional row selection, paging, and an optional totals row.
 *
 * Why not the library's Table / DataTable: Table has no loading, selection or
 * paging; DataTable keeps its selection INTERNAL, so a page cannot clear it
 * after deleting the selected rows (the old keys would linger), and it has no
 * paging either. This is a deliberate, scoped exception to "prefer a library
 * component" (.claude/rules/components.md) — the LOOK is still the library's:
 * the markup reuses its `amd-tbl` table classes and `amd-sk` shimmer, and
 * paging is the library's Pagination.
 *
 * Usage:
 *   <Table
 *     caption="Instances"
 *     columns={[{ key: 'region', header: 'Region' },
 *               { key: 'cpu', header: 'CPU (%)', numeric: true,
 *                 render: (row) => row.cpu }]}
 *     rows={rows}
 *     rowKey={(row) => row.id}
 *     loading={isLoading}
 *     selectable selectedKeys={keys} onSelectionChange={setKeys}
 *   />
 *
 * Selection is CONTROLLED (selectedKeys / onSelectionChange) so the owner can
 * clear it. "Select all" covers the CURRENT PAGE, as legacy's v-data-table
 * did (InstancesListTable.vue toggleSelectAll).
 *
 * Paging is LOCAL by default: all rows are given and the table slices them.
 * SERVER paging: pass `total` (the number of rows on the server) and `rows`
 * is taken as the current page as-is — nothing is sliced; the pager counts
 * from `total`. `page` (1-based) and `pageSize` may then be controlled, with
 * `onPageChange(page)` / `onPageSizeChange(size)` to fetch another page (on a
 * size change the owner goes back to page 1). Without them the table keeps
 * its own page / size and still reports changes. The server pager stays
 * visible while a page loads, so the user keeps their place.
 *
 * Grouped headers: give consecutive columns the same `group` label and the
 * header becomes two rows — the group label spanning those columns on top,
 * their own headers under it; ungrouped columns (and the checkbox) span both
 * rows. Legacy's instance table does this with "Maximum Bandwidth Used". The
 * first column of a run may carry `groupHeader` (a node, e.g. the label with
 * a tooltip) to draw instead of the plain `group` text.
 *
 * Totals row: `footerRow` = { [column key]: node } is drawn as a <tfoot> row
 * under the rows, each value in its column (numeric alignment kept, an empty
 * cell under the checkbox); a column not in it is left blank. Shown only when
 * there are rows.
 */
import { useEffect, useRef, useState } from 'react';

import { Dropdown } from '@components/common/Dropdown';
import { Pagination } from '@components/ui';
import { cx } from '@lib/classNames';

import styles from './Table.module.css';

/** Shimmer widths, cycled per cell so the skeleton does not look like a grid of bricks. */
const SKELETON_WIDTHS = ['70%', '45%', '85%', '55%', '65%'];

/**
 * The top header row for grouped columns: one cell per run of consecutive
 * columns sharing a `group`, and one per ungrouped column (spanning both rows).
 */
function headerRuns(columns) {
  const runs = [];
  for (const col of columns) {
    const last = runs[runs.length - 1];
    if (col.group && last?.group === col.group) last.span += 1;
    else runs.push({ group: col.group ?? null, span: 1, col });
  }
  return runs;
}

/** A header checkbox that can show "some selected". */
function SelectAllCheckbox({ checked, indeterminate, onChange, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className={styles.checkbox}
      checked={checked}
      onChange={onChange}
      aria-label={label}
    />
  );
}

export function Table({
  columns,
  rows = [],
  rowKey,
  caption,
  loading = false,
  skeletonRows = 5,
  emptyMessage = 'No data to show.',
  selectable = false,
  selectedKeys = [],
  onSelectionChange,
  paginate = true,
  pageSizeOptions = [10, 25, 50, 100],
  defaultPageSize = 10,
  total,
  page: pageProp,
  pageSize: pageSizeProp,
  onPageChange,
  onPageSizeChange,
  footerRow,
  getRowClassName,
  className,
}) {
  const [ownPageSize, setOwnPageSize] = useState(defaultPageSize);
  const [ownPage, setOwnPage] = useState(1);
  const pageSize = pageSizeProp ?? ownPageSize;

  // Server paging: `rows` is one page; `total` counts them all.
  const server = typeof total === 'number' && Number.isFinite(total);
  const count = server ? Math.max(0, total) : rows.length;

  // Clamp rather than reset: deleting rows on the last page should land on the
  // new last page, not jump back to page 1.
  const pageCount = paginate ? Math.max(1, Math.ceil(count / pageSize)) : 1;
  const currentPage = Math.min(Math.max(1, pageProp ?? ownPage), pageCount);
  const start = paginate ? (currentPage - 1) * pageSize : 0;
  const visibleRows = paginate && !server ? rows.slice(start, start + pageSize) : rows;

  const changePage = (next) => {
    setOwnPage(next);
    onPageChange?.(next);
  };
  const changePageSize = (next) => {
    setOwnPageSize(next);
    setOwnPage(1);
    onPageSizeChange?.(next);
  };

  const selected = new Set(selectedKeys);
  const pageKeys = visibleRows.map(rowKey);
  const selectedOnPage = pageKeys.filter((k) => selected.has(k)).length;
  const allOnPage = pageKeys.length > 0 && selectedOnPage === pageKeys.length;

  const toggleRow = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange?.([...next]);
  };

  const togglePage = () => {
    const next = new Set(selected);
    for (const key of pageKeys) {
      if (allOnPage) next.delete(key);
      else next.add(key);
    }
    onSelectionChange?.([...next]);
  };

  const colSpan = columns.length + (selectable ? 1 : 0);
  const grouped = columns.some((col) => col.group);

  /** One column's own header cell; `rowSpan` 2 when it stands beside a group. */
  const headerCell = (col, rowSpan) => (
    <th
      key={col.key}
      scope="col"
      rowSpan={rowSpan}
      className={cx(col.numeric && 'amd-tbl__num', col.headerClassName)}
      style={col.width ? { width: col.width } : undefined}
    >
      {col.header}
    </th>
  );

  let body;
  if (loading) {
    body = Array.from({ length: skeletonRows }, (_, r) => (
      <tr key={`skeleton-${r}`} aria-hidden="true">
        {selectable ? (
          <td className={styles.checkCell}>
            <span className={cx('amd-sk', styles.skeletonBox)} />
          </td>
        ) : null}
        {columns.map((col, c) => (
          <td key={col.key}>
            <span
              className={cx('amd-sk', styles.skeleton)}
              style={{ width: SKELETON_WIDTHS[(r + c) % SKELETON_WIDTHS.length] }}
            />
          </td>
        ))}
      </tr>
    ));
  } else if (rows.length === 0) {
    body = (
      <tr>
        <td colSpan={colSpan} className={styles.empty}>
          {emptyMessage}
        </td>
      </tr>
    );
  } else {
    body = visibleRows.map((row) => {
      const key = rowKey(row);
      const isSelected = selected.has(key);
      return (
        <tr
          key={key}
          className={cx(isSelected && styles.selectedRow, getRowClassName?.(row))}
          aria-selected={selectable ? isSelected : undefined}
        >
          {selectable ? (
            <td className={styles.checkCell}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={isSelected}
                onChange={() => toggleRow(key)}
                aria-label="Select row"
              />
            </td>
          ) : null}
          {columns.map((col) => (
            <td
              key={col.key}
              className={cx(col.numeric && 'amd-tbl__num', col.className)}
              style={col.width ? { width: col.width } : undefined}
            >
              {col.render ? col.render(row) : (row[col.key] ?? '—')}
            </td>
          ))}
        </tr>
      );
    });
  }

  // Local: hidden while loading or empty. Server: kept while the next page
  // loads (the rows are the server's, not ours to count).
  const showFooter = paginate && (server ? count > 0 : !loading && rows.length > 0);
  const firstShown = count ? start + 1 : 0;
  const lastShown = Math.min(start + pageSize, count);

  const showTotals = Boolean(footerRow) && rows.length > 0;

  // One framed box: the scrolling table on top, the paging footer inside the
  // frame underneath — the app's table design.
  return (
    <div className={cx(styles.frame, className)}>
      <div className={styles.scroll}>
        <table className={cx('amd-tbl', styles.table)} aria-busy={loading || undefined}>
          {caption ? <caption className="sr-only">{caption}</caption> : null}
          <thead>
            <tr>
              {selectable ? (
                <th className={styles.checkCell} scope="col" rowSpan={grouped ? 2 : undefined}>
                  <SelectAllCheckbox
                    checked={allOnPage}
                    indeterminate={selectedOnPage > 0 && !allOnPage}
                    onChange={togglePage}
                    label="Select all rows on this page"
                  />
                </th>
              ) : null}
              {grouped
                ? headerRuns(columns).map((run) =>
                    run.group ? (
                      <th
                        key={`group-${run.col.key}`}
                        scope="colgroup"
                        colSpan={run.span}
                        className={styles.groupHeader}
                      >
                        {run.col.groupHeader ?? run.group}
                      </th>
                    ) : (
                      headerCell(run.col, 2)
                    ),
                  )
                : columns.map((col) => headerCell(col))}
            </tr>
            {grouped ? (
              <tr>{columns.filter((col) => col.group).map((col) => headerCell(col))}</tr>
            ) : null}
          </thead>
          <tbody>{body}</tbody>
          {showTotals ? (
            <tfoot>
              <tr className={styles.totalsRow}>
                {selectable ? <td className={styles.checkCell} /> : null}
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cx(col.numeric && 'amd-tbl__num', col.className)}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {footerRow[col.key] ?? null}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {showFooter ? (
        <div className={styles.footer}>
          {/* Left: where you are, and how many per page. */}
          <div className={styles.footerInfo}>
            <span className={styles.range} aria-live="polite">
              Showing {firstShown}–{lastShown} of {count}
            </span>
            {/* The app's Dropdown, not a native <select>, so it matches every
                other picker in the app. */}
            <div className={styles.pageSize}>
              <span aria-hidden="true">Rows per page</span>
              <Dropdown
                className={styles.pageSizeField}
                options={pageSizeOptions}
                value={pageSize}
                onChange={(next) => changePageSize(Number(next))}
                hideDetails
                aria-label="Rows per page"
              />
            </div>
          </div>
          {/* Right: the pages — shown even for one page, as in the design. */}
          <Pagination count={pageCount} page={currentPage} onChange={changePage} />
        </div>
      ) : null}
    </div>
  );
}

export default Table;
